import { lazy, Suspense } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'

import { PortalLayout } from '@/layouts/portal-layout'
import { AuthLayout } from '@/layouts/auth-layout'
import { AuthProvider } from '@/providers/auth-context'
import { PermissionProvider } from '@/providers/permission-provider'
import { ConfigProvider } from '@/providers/config-provider'

const Loading = () => <div className="flex h-screen items-center justify-center">Carregando...</div>

// Auth pages
const LoginPage = lazy(() => import('@/pages/auth/login'))

// Portal pages
const DashboardPage = lazy(() => import('@/pages/portal/dashboard'))
const ChargesPage = lazy(() => import('@/pages/portal/charges'))
const ChargeDetailsPage = lazy(() => import('@/pages/portal/charges/details'))
const CustomersPage = lazy(() => import('@/pages/portal/customers'))
const BrokersPage = lazy(() => import('@/pages/portal/brokers'))
const BrokerDetailsPage = lazy(() => import('@/pages/portal/brokers/details'))
const AccessManagementPage = lazy(() => import('@/pages/portal/access-management'))
const UploadPage = lazy(() => import('@/pages/portal/upload'))
const UploadDetailsPage = lazy(() => import('@/pages/portal/upload/details'))
const ExportedReportsPage = lazy(() => import('@/pages/portal/exported-reports'))
const ExportedReportDetailsPage = lazy(() => import('@/pages/portal/exported-reports/details'))
const DomainPreferencesPage = lazy(() => import('@/pages/portal/domain-preferences'))
const DomainsPreferencesPage = lazy(() => import('@/pages/portal/domains/preferences'))
const Pay2bCredentialsPage = lazy(() => import('@/pages/portal/pay2b-credentials'))
const TransferSchedulePage = lazy(() => import('@/pages/portal/transfer-schedule'))
const TransferScheduleDetailsPage = lazy(() => import('@/pages/portal/transfer-schedule/details'))
const TransferScheduleReleasesPage = lazy(() => import('@/pages/portal/transfer-schedule/releases'))

// Report pages
const ReportCommissionsPage = lazy(() => import('@/pages/portal/report/commissions'))
const ReportDefaultersPage = lazy(() => import('@/pages/portal/report/defaulters'))

// Report Delivery pages
const ReportDeliveryDashboardPage = lazy(() => import('@/pages/portal/report-delivery'))
const TransferConfigsPage = lazy(() => import('@/pages/portal/report-delivery/transfer-configs'))
const NewTransferConfigPage = lazy(() => import('@/pages/portal/report-delivery/transfer-configs/new'))
const TransferConfigDetailsPage = lazy(() => import('@/pages/portal/report-delivery/transfer-configs/details'))
const EditTransferConfigPage = lazy(() => import('@/pages/portal/report-delivery/transfer-configs/edit'))
const SchedulesPage = lazy(() => import('@/pages/portal/report-delivery/schedules'))
const NewSchedulePage = lazy(() => import('@/pages/portal/report-delivery/schedules/new'))
const ScheduleDetailsPage = lazy(() => import('@/pages/portal/report-delivery/schedules/details'))
const EditSchedulePage = lazy(() => import('@/pages/portal/report-delivery/schedules/edit'))
const ProcessPage = lazy(() => import('@/pages/portal/report-delivery/process'))

// Webhooks pages
const WebhooksPage = lazy(() => import('@/pages/portal/webhooks'))
const WebhookDetailsPage = lazy(() => import('@/pages/portal/webhooks/details'))
const NewWebhookPage = lazy(() => import('@/pages/portal/webhooks/new'))
const EditWebhookPage = lazy(() => import('@/pages/portal/webhooks/edit'))
const WebhookResourcesPage = lazy(() => import('@/pages/portal/webhooks/resources'))
const WebhookResourceDetailsPage = lazy(() => import('@/pages/portal/webhooks/resources/details'))

// SSO Hand-off API keys (admin)
const SsoHandoffApiKeysPage = lazy(() => import('@/pages/portal/sso-handoff-api-keys'))

// SSO Providers (admin)
const SsoProvidersPage = lazy(() => import('@/pages/portal/sso-providers'))
const NewSsoProviderPage = lazy(() => import('@/pages/portal/sso-providers/new'))
const EditSsoProviderPage = lazy(() => import('@/pages/portal/sso-providers/edit'))

// Chromium (admin)
const ChromiumPage = lazy(() => import('@/pages/portal/chromium'))

const withSuspense = (Component: React.LazyExoticComponent<() => React.ReactElement>) => (
  <Suspense fallback={<Loading />}>
    <Component />
  </Suspense>
)

export const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <Suspense fallback={<Loading />}>
        <ConfigProvider>
          <AuthProvider>
            <AuthLayout />
          </AuthProvider>
        </ConfigProvider>
      </Suspense>
    ),
    children: [
      { index: true, element: withSuspense(LoginPage) },
    ],
  },
  {
    path: '/portal',
    element: (
      <Suspense fallback={<Loading />}>
        <ConfigProvider>
          <AuthProvider>
            <PermissionProvider>
              <PortalLayout />
            </PermissionProvider>
          </AuthProvider>
        </ConfigProvider>
      </Suspense>
    ),
    children: [
      { index: true, element: withSuspense(DashboardPage) },
      { path: 'charges', element: withSuspense(ChargesPage) },
      { path: 'charges/:id', element: withSuspense(ChargeDetailsPage) },
      { path: 'customers', element: withSuspense(CustomersPage) },
      { path: 'brokers', element: withSuspense(BrokersPage) },
      { path: 'brokers/:brokerUuid', element: withSuspense(BrokerDetailsPage) },
      { path: 'access-management', element: withSuspense(AccessManagementPage) },
      { path: 'upload', element: withSuspense(UploadPage) },
      { path: 'upload/:id', element: withSuspense(UploadDetailsPage) },
      { path: 'exported-reports', element: withSuspense(ExportedReportsPage) },
      { path: 'exported-reports/:uuid', element: withSuspense(ExportedReportDetailsPage) },
      { path: 'domain-preferences', element: withSuspense(DomainPreferencesPage) },
      { path: 'domains/:domainId/preferences', element: withSuspense(DomainsPreferencesPage) },
      { path: 'pay2b-credentials', element: withSuspense(Pay2bCredentialsPage) },
      { path: 'transfer-schedule', element: withSuspense(TransferSchedulePage) },
      { path: 'transfer-schedule/releases', element: withSuspense(TransferScheduleReleasesPage) },
      { path: 'transfer-schedule/:id', element: withSuspense(TransferScheduleDetailsPage) },
      // Reports
      { path: 'report/commissions', element: withSuspense(ReportCommissionsPage) },
      { path: 'report/defaulters', element: withSuspense(ReportDefaultersPage) },
      // Report Delivery
      { path: 'report-delivery', element: withSuspense(ReportDeliveryDashboardPage) },
      { path: 'report-delivery/transfer-configs', element: withSuspense(TransferConfigsPage) },
      { path: 'report-delivery/transfer-configs/new', element: withSuspense(NewTransferConfigPage) },
      { path: 'report-delivery/transfer-configs/:uuid', element: withSuspense(TransferConfigDetailsPage) },
      { path: 'report-delivery/transfer-configs/:uuid/edit', element: withSuspense(EditTransferConfigPage) },
      { path: 'report-delivery/schedules', element: withSuspense(SchedulesPage) },
      { path: 'report-delivery/schedules/new', element: withSuspense(NewSchedulePage) },
      { path: 'report-delivery/schedules/:id', element: withSuspense(ScheduleDetailsPage) },
      { path: 'report-delivery/schedules/:id/edit', element: withSuspense(EditSchedulePage) },
      { path: 'report-delivery/process', element: withSuspense(ProcessPage) },
      // Webhooks
      { path: 'webhooks', element: withSuspense(WebhooksPage) },
      { path: 'webhooks/new', element: withSuspense(NewWebhookPage) },
      { path: 'webhooks/:webhookId', element: withSuspense(WebhookDetailsPage) },
      { path: 'webhooks/:webhookId/edit', element: withSuspense(EditWebhookPage) },
      { path: 'webhooks/:webhookId/resources', element: withSuspense(WebhookResourcesPage) },
      { path: 'webhooks/:webhookId/resources/:resourceId', element: withSuspense(WebhookResourceDetailsPage) },
      // SSO Hand-off API keys (admin)
      { path: 'sso-handoff-api-keys', element: withSuspense(SsoHandoffApiKeysPage) },
      // SSO Providers (admin)
      { path: 'sso-providers', element: withSuspense(SsoProvidersPage) },
      { path: 'sso-providers/new', element: withSuspense(NewSsoProviderPage) },
      { path: 'sso-providers/:id/edit', element: withSuspense(EditSsoProviderPage) },
      // Chromium (admin)
      { path: 'chromium', element: withSuspense(ChromiumPage) },
    ],
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
])
